import { log } from '@/lib/logger'
import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { COLOR_DE_ESTADO, COLOR_DE_ESTADO_POR_DEFECTO } from '@/lib/colores-de-estado'
import { cn } from '@/lib/utils'
import { getStatusStyles, getStatusDisplayName } from '@/lib/conversation-types'
import type { ConversationStatus } from '@/lib/conversation-types'

// Solo los estados que se pueden cambiar desde el select
type SelectableStatus = 'active' | 'needs_review' | 'archived' | 'completed'
import { useTranslations } from '@/hooks/use-translations'

interface ConversationStatusSelectProps {
  conversationId: string
  currentStatus: ConversationStatus
  onStatusChange: (conversationId: string, newStatus: SelectableStatus) => void
  disabled?: boolean
  className?: string
}

const STATUS_OPTIONS: Array<{
  value: SelectableStatus
  label: string
  color: string
  description: string
}> = [
  { value: 'active', label: 'Activo', color: 'green', description: 'Conversación activa' },
  { value: 'needs_review', label: 'Necesita Revisión', color: 'orange', description: 'Requiere revisión manual' },
  { value: 'archived', label: 'Archivada', color: 'gray', description: 'Conversación archivada' },
  { value: 'completed', label: 'Completada', color: 'blue', description: 'Conversación completada' }
]

export function ConversationStatusSelect({
  conversationId,
  currentStatus,
  onStatusChange,
  disabled = false,
  className
}: ConversationStatusSelectProps) {
  const { t } = useTranslations()
  const [isUpdating, setIsUpdating] = useState(false)

  const currentOption = STATUS_OPTIONS.find(option => option.value === currentStatus)

  const handleStatusChange = async (newStatus: ConversationStatus) => {
    if (newStatus === currentStatus || disabled || isUpdating) return

    log.debug('🔄 Changing status:', {
      conversationId,
      currentStatus,
      newStatus,
      disabled,
      isUpdating
    })

    // Solo permitir estados seleccionables (no ai_responding)
    if (newStatus === 'ai_responding') {
      console.warn('⚠️ Cannot change status to ai_responding from select')
      return
    }

    setIsUpdating(true)
    try {
      await onStatusChange(conversationId, newStatus as SelectableStatus)
      log.debug('✅ Status change successful:', newStatus)
    } catch (error) {
      console.error('❌ Error updating conversation status:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const getColorClass = (color: string) => COLOR_DE_ESTADO[color] || COLOR_DE_ESTADO_POR_DEFECTO

  const Punto = ({ color }: { color: string }) => (
    <span className={cn('h-2 w-2 shrink-0 rounded-full', getColorClass(color))} />
  )

  // Era un desplegable hecho a mano: `createPortal`, un fondo invisible que
  // cerraba al pulsar, dos `useEffect` para cerrarlo al hacer scroll, la
  // posicion calculada con `getBoundingClientRect` y `z-[100000]`. Todo eso lo
  // hace Radix, que ademas trae el teclado y el foco -que la version a mano no
  // tenia-.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="xs"
          disabled={disabled || isUpdating}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'gap-2 font-medium',
            getStatusStyles(currentStatus),
            isUpdating && 'animate-pulse',
            className,
          )}
        >
          <Punto color={currentOption?.color || 'gray'} />
          <span>{getStatusDisplayName(currentStatus, t)}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-w-[200px]">
        {STATUS_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            disabled={option.value === currentStatus || isUpdating}
            onSelect={() => { void handleStatusChange(option.value) }}
            className="gap-2"
          >
            <Punto color={option.color} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium">{option.label}</span>
              <span className="truncate text-xs text-muted-foreground">{option.description}</span>
            </span>
            {option.value === currentStatus && (
              <Check className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
